import { auth } from './firebase';
import { signOut } from 'firebase/auth';

/** The api's discriminator for a platform-wide account suspension. */
const ACCOUNT_DEACTIVATED = 'ACCOUNT_DEACTIVATED';

function isDeactivated(body: unknown): boolean {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    (body as { error?: unknown }).error === ACCOUNT_DEACTIVATED
  );
}

/**
 * Sign the browser out, once.
 *
 * Guarded because the app fires many queries in parallel: without
 * the latch every one of them would call signOut on the same tick.
 */
let signingOut = false;
async function signOutOnDeactivation(): Promise<void> {
  if (signingOut) return;
  signingOut = true;
  try {
    await signOut(auth);
  } catch {
    // Nothing useful to do — the api is refusing this account either
    // way, and throwing here would replace a clear 403 with a confusing
    // sign-out error.
    //
    // RELEASE THE LATCH on failure. It exists only to collapse the burst
    // of parallel queries that all 403 at once; leaving it set after a
    // failed sign-out would disarm every LATER attempt for the lifetime
    // of the tab, so a transient failure would strand the session
    // signed-in against an api that refuses it.
    signingOut = false;
  }
}

// /v1 — api versioned at the path level (the subdomain is already
// `*-api.…`). When the server rev'd from /api to /v1, this default
// rev'd with it.
const BASE_URL = import.meta.env.VITE_API_URL ?? '/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API ${status}`);
  }
}

/**
 * Thin fetch wrapper that attaches the current Firebase ID token and
 * normalizes errors. Use TanStack Query in components — this is the
 * fetch primitive Query Functions call.
 *
 * Accepts an optional `body` that gets JSON-serialized automatically.
 * Pass the raw `RequestInit.body` (Blob, FormData, etc.) only via the
 * standard `init.body` field if you need non-JSON.
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: Omit<RequestInit, 'body'> & { body?: unknown } = {},
): Promise<T> {
  return apiFetchImpl<T>(path, init, false);
}

async function apiFetchImpl<T>(
  path: string,
  init: Omit<RequestInit, 'body'> & { body?: unknown },
  alreadyRetried: boolean,
): Promise<T> {
  const { body, headers: headersInit, ...rest } = init;
  // `getIdToken()` returns the cached token until it's within 5 minutes
  // of expiry, at which point Firebase Web SDK auto-refreshes. Force a
  // refresh on retry in case the api saw expiry first (clock skew).
  const token = await auth.currentUser?.getIdToken(alreadyRetried);
  const headers = new Headers(headersInit);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const fetchInit: RequestInit = {
    ...rest,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  };

  const res = await fetch(`${BASE_URL}${path}`, fetchInit);

  // 401 from a signed-in caller usually means the api rejected an
  // about-to-expire token. Force-refresh once and retry — if the second
  // attempt also 401s, the user genuinely isn't authenticated and the
  // error bubbles up to the route guard, which redirects to /.
  if (res.status === 401 && auth.currentUser && !alreadyRetried) {
    return apiFetchImpl<T>(path, init, true);
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    // ACCOUNT-LEVEL SUSPENSION. Every request this session makes from
    // here on will 403 identically, so retrying or letting each screen
    // render its own "something went wrong" is noise around a fact the
    // citizen needs told once: they have been signed out. Sign out
    // rather than leaving them tapping at a broken app until their ID
    // token expires (up to an hour).
    //
    // NOTE this is the ACCOUNT-level switch, not a per-city report
    // block. A blocked citizen stays signed in and is told so when they
    // try to file a report — that block expires and is appealable; this
    // one is neither.
    if (res.status === 403 && isDeactivated(errBody)) {
      void signOutOnDeactivation();
    }
    throw new ApiError(res.status, errBody);
  }
  if (res.status === 204) return undefined as T;

  // An endpoint that legitimately resolves to "nothing" — GET
  // /issues/:id/voice-message on a report with no voice note — returns
  // `null` from its handler, and Nest's Express adapter turns a nil return
  // into a 200 with a ZERO-BYTE body. `res.json()` on that throws
  // SyntaxError, which surfaces as a query error indistinguishable from a
  // real outage: VoiceNote reads only `voice.data`, so the citizen was
  // told "no voice note" while the query sat permanently failed. Map the
  // empty body to `null` — never `undefined`, which TanStack Query v5
  // rejects outright as a queryFn result.
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/**
 * Detect whether a string is shaped like an E.164 phone number.
 *   +14165551234   → true
 *   yousef@geoxity.co → false
 */
export function isPhoneLike(value: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test(value.replace(/\s|-|\(|\)/g, ''));
}

/**
 * Detect whether a string is shaped like an email address.
 * Loose RFC 5322 — good enough for client-side classification before the
 * server does its own strict validation.
 */
export function isEmailLike(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}
